import { useState, useEffect } from 'react';
import { PivotTableData } from '../models/PulseData';
import { FetchState } from '../models/commonTypes';
import { CustomerInfo, getServicesForGroupId } from '../models/CustomerData';
import { fetchPulsePivotTableData } from '../services/pulseService';
import { samplePivotData } from '../data/PulseContent.samples';
import { useCustomer } from '../contexts/CustomerContext';
import { TimeSliceWindow } from '@/models/TimeSlice';
import logger from '../utils/logger';

interface UsePulsePivotDataProps {
  selectedCustomer: CustomerInfo | null;
  selectedServicesGroupId: string | null;
  timeSliceWindow: TimeSliceWindow;
}

export const usePulsePivotData = ({
  selectedCustomer,
  selectedServicesGroupId,
  timeSliceWindow,
}: UsePulsePivotDataProps): FetchState<PivotTableData> => {

  const [pivotTableState, setPivotTableState] = useState<FetchState<PivotTableData>>({
    data: null,
    isLoading: true,
    error: null,
  });
  const { selectedApiKey} = useCustomer();

  useEffect(() => {
    if (!selectedCustomer || !selectedServicesGroupId) {
      setPivotTableState({ data: null, isLoading: false, error: null });
      return;
    }

    const fetchInitialPivotData = async () => {
      setPivotTableState({ data: null, isLoading: true, error: null }); // Reset data when fetching

      try {
        const params = {
          apiKey: selectedApiKey,
          customer: selectedCustomer.id,
          servicesList: getServicesForGroupId(selectedCustomer, selectedServicesGroupId),
          attribute: null,
          startDate: timeSliceWindow.startTime,
          endDate: timeSliceWindow.endTime,
          timeIncrement: timeSliceWindow.incrementMinutes
        };
        const data = await fetchPulsePivotTableData(params);

        if (!data || data.message !== 'success') {
          throw new Error(`Fetching Pivot table failed with error message: ${data.message}`);
        }
        setPivotTableState({ data: data.pivotTable, isLoading: false, error: null });
        logger.info('Successfully fetched and set pivot table data.');

      } catch (err) {
        
        logger.error('Failed to fetch pivot data:', err);
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred while fetching pivot table data.';
        setPivotTableState({ data: samplePivotData.pivotTable, isLoading: false, error: errorMessage }); // Fallback
        logger.warn('Falling back to sample pivot data due to fetch error.');
      }
    };

    fetchInitialPivotData();
  }, [selectedCustomer, selectedApiKey, selectedServicesGroupId, timeSliceWindow]);

  return pivotTableState;
};